import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Shield, Database, Terminal, Network, Zap, Search } from "lucide-react";
import { toast } from "../../components/ui/use-toast";
import NavBar from '../../components/NavBar';
import { getInvitationsForUser } from '../../lib/huntersBoardService';
import { useUserName } from '../../lib/useUserName';

export const ToolkitPage = (): JSX.Element => {
  const navigate = useNavigate();
  const userName = useUserName();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lolbasSettings, setLolbasSettings] = useState<{ enabled: boolean; description: string } | null>(null);
  const [gtfobinsSettings, setGtfobinsSettings] = useState<{ enabled: boolean; description: string } | null>(null);
  const [wadcomsSettings, setWadcomsSettings] = useState<{ enabled: boolean; description: string } | null>(null);
  const [reverseShellSettings, setReverseShellSettings] = useState<{ enabled: boolean; description: string } | null>(null);
  const [osintSettings, setOsintSettings] = useState<{ enabled: boolean; description: string } | null>(null);
  const [inviteCount, setInviteCount] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/signin');
      } else {
        setUserId(user.uid);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!userId) return;
    getInvitationsForUser(userId).then(invites => {
      setInviteCount(invites.length);
    });
  }, [userId]);

  // Load LOLBAS settings on component mount
  useEffect(() => {
    const loadLOLBASSettings = async () => {
      try {
        const settings = await methodologyService.getLOLBASSettings();
        setLolbasSettings(settings);
      } catch (error) {
        console.error("Failed to load LOLBAS settings:", error);
      }
    };

    const loadGTFOBinsSettings = async () => {
      try {
        const settings = await methodologyService.getSystemSettings();
        setGtfobinsSettings({
          enabled: !settings.disableGTFOBins,
          description: settings.gtfobinsDescription || "Unix binaries for bypassing local security restrictions"
        });
      } catch (error) {
        console.error("Failed to load GTFOBins settings:", error);
      }
    };

    const loadWADComsSettings = async () => {
      try {
        const settings = await methodologyService.getSystemSettings();
        setWadcomsSettings({
          enabled: !settings.disableWADComs,
          description: settings.wadcomsDescription || "Windows Active Directory commands and techniques for security testing"
        });
      } catch (error) {
        console.error("Failed to load WADComs settings:", error);
      }
    };

    const loadReverseShellSettings = async () => {
      try {
        const settings = await methodologyService.getSystemSettings();
        setReverseShellSettings({
          enabled: !settings.disableReverseShell,
          description: settings.reverseShellDescription || "Generate reverse shell payloads for various platforms and languages"
        });
      } catch (error) {
        console.error("Failed to load Reverse Shell settings:", error);
      }
    };

    const loadOSINTSettings = async () => {
      try {
        const settings = await methodologyService.getSystemSettings();
        setOsintSettings({
          enabled: !settings.disableOSINT,
          description: settings.osintDescription || "A collection of essential OSINT tools, commands, and links for reconnaissance."
        });
      } catch (error) {
        console.error("Failed to load OSINT settings:", error);
      }
    };

    loadLOLBASSettings();
    loadGTFOBinsSettings();
    loadWADComsSettings();
    loadReverseShellSettings();
    loadOSINTSettings();
  }, []);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <NavBar
        userName={userName}
        onSignOut={handleSignOut}
        inviteCount={inviteCount}
      />

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[180px] pb-12">
        {/* Mobile Back Button */}
        <div className="lg:hidden mb-4">
          <button 
            onClick={() => navigate('/dashboard')}
            className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
          >
            ← Back to Dashboard
          </button>
        </div>
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Toolkit selection page */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                  Pentest Toolkits
                </h1>
                <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                  Choose a toolkit to access cybersecurity tools and commands
                </p>
              </div>
            </div>
          </div>

          {/* Toolkit Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Hunting Toolkit Card */}
            <Card className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer" 
                  onClick={() => navigate('/toolkits/hunting-toolkit')}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                    <Shield className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-black">Hunting Toolkit</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Curated pentest tools and commands</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Access organized collections of cybersecurity tools and commands for penetration testing engagements.
                </p>
                <Button 
                  className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/toolkits/hunting-toolkit');
                  }}
                >
                  Access Hunting Toolkit
                </Button>
              </CardContent>
            </Card>

            {/* LOLBAS Card */}
            {lolbasSettings?.enabled && (
              <Card className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/toolkits/lolbas')}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                      <Database className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-black">LOLBAS</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Living Off The Land Binaries</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {lolbasSettings.description}
                  </p>
                  <Button 
                    className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/toolkits/lolbas');
                    }}
                  >
                    Access LOLBAS
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* GTFOBins Card */}
            {gtfobinsSettings?.enabled && (
              <Card className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/toolkits/gtfobins')}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                      <Terminal className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-black">GTFOBins</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Unix binaries for bypassing local security restrictions</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {gtfobinsSettings.description}
                  </p>
                  <Button 
                    className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/toolkits/gtfobins');
                    }}
                  >
                    Access GTFOBins
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* WADComs Card */}
            {wadcomsSettings?.enabled && (
              <Card className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/toolkits/wadcoms')}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                      <Network className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-black">WADComs</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Windows Active Directory commands and techniques for security testing</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {wadcomsSettings.description}
                  </p>
                  <Button 
                    className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/toolkits/wadcoms');
                    }}
                  >
                    Access WADComs
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Reverse Shell Generator Card */}
            {reverseShellSettings?.enabled && (
              <Card className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/toolkits/reverse-shell')}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                      <Zap className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-black">Reverse Shell Generator</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Generate reverse shell payloads</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {reverseShellSettings.description}
                  </p>
                  <Button 
                    className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/toolkits/reverse-shell');
                    }}
                  >
                    Access Reverse Shell Generator
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* OSINT Toolkit Card */}
            {osintSettings?.enabled && (
              <Card className="border-2 border-[#E7E7E7] hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate('/toolkits/osint')}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                      <Search className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-black">OSINT Toolkit</CardTitle>
                      <p className="text-sm text-gray-600 mt-1">Open-Source Intelligence tools & checklists</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {osintSettings.description}
                  </p>
                  <Button 
                    className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/toolkits/osint');
                    }}
                  >
                    Access OSINT Toolkit
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Placeholder for future toolkits */}
            <Card className="border-2 border-dashed border-gray-300 bg-gray-50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-300 rounded-lg flex items-center justify-center">
                    <div className="w-6 h-6 bg-gray-400 rounded"></div>
                  </div>
                  <div>
                    <CardTitle className="text-xl text-gray-500">More Coming Soon</CardTitle>
                    <p className="text-sm text-gray-400 mt-1">Additional toolkits will appear here</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">
                  New cybersecurity toolkits and resources will be added here as they become available.
                </p>
                <Button 
                  className="w-full bg-gray-300 text-gray-500 cursor-not-allowed"
                  disabled
                >
                  Coming Soon
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 